import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

const FALLBACK_LOCATION = {
  lat: 35.4834,
  lng: -86.4603,
  label: "Bedford County, Tennessee",
};

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const getSeason = (month) => {
  if ([11, 0, 1].includes(month)) return "Winter";
  if ([2, 3, 4].includes(month)) return "Spring";
  if ([5, 6, 7].includes(month)) return "Summer";
  return "Fall";
};

const getWeatherCodeLabel = (code) => {
  if (code === 0) return "Clear";
  if (code === 1) return "Mainly clear";
  if (code === 2) return "Partly cloudy";
  if (code === 3) return "Overcast";
  if ([45, 48].includes(code)) return "Fog";
  if ([51, 53, 55].includes(code)) return "Drizzle";
  if ([61, 63, 65].includes(code)) return "Rain";
  if ([66, 67].includes(code)) return "Freezing rain";
  if ([71, 73, 75].includes(code)) return "Snow";
  if ([80, 81, 82].includes(code)) return "Rain showers";
  if (code === 95) return "Thunderstorms";
  if ([96, 99].includes(code)) return "Thunderstorms with hail";
  return "Weather update";
};

const getWeatherIcon = (code, rainChance = 0) => {
  const weatherCode = Number(code);
  const chance = Number(rainChance) || 0;

  if ([95, 96, 99].includes(weatherCode)) return "\u26C8\uFE0F";
  if ([71, 73, 75].includes(weatherCode)) return "\u2744\uFE0F";
  if ([66, 67].includes(weatherCode)) return "\uD83E\uDDCA";
  if ([61, 63, 65, 80, 81, 82].includes(weatherCode) || chance >= 55) {
    return "\uD83C\uDF27\uFE0F";
  }
  if ([51, 53, 55].includes(weatherCode) || chance >= 30) return "\uD83C\uDF26\uFE0F";
  if ([45, 48].includes(weatherCode)) return "\uD83C\uDF2B\uFE0F";
  if (weatherCode === 3) return "\u2601\uFE0F";
  if ([1, 2].includes(weatherCode)) return "\u26C5";
  if (weatherCode === 0) return "\u2600\uFE0F";
  return "\uD83C\uDF24\uFE0F";
};

const getForecastWeatherLabel = (code, rainChance = 0, index = 0) => {
  if (index === 0 && Number.isFinite(Number(code))) {
    return getWeatherCodeLabel(Number(code));
  }

  const chance = Number(rainChance) || 0;
  if (chance >= 60) return "Rain likely";
  if (chance >= 35) return "Rain possible";
  if (chance >= 15) return "Cloud watch";
  return "Fair outlook";
};

const getFrostRisk = (month, currentTemp, dailyLow) => {
  if (Number.isFinite(currentTemp) && currentTemp <= 32) {
    return {
      label: "High",
      tone: "high",
      note: "Current temperature is at or below freezing.",
    };
  }

  if (Number.isFinite(dailyLow) && dailyLow <= 32) {
    return {
      label: "High",
      tone: "high",
      note: "Today's low is at or below freezing.",
    };
  }

  if (Number.isFinite(currentTemp) && currentTemp <= 38) {
    return {
      label: "Watch",
      tone: "watch",
      note: "Current temperature is close enough to watch tender plants.",
    };
  }

  if (Number.isFinite(dailyLow) && dailyLow <= 38) {
    return {
      label: "Watch",
      tone: "watch",
      note: "Today's low is close enough to keep covers handy.",
    };
  }

  if ([11, 0, 1].includes(month)) {
    return {
      label: "High",
      tone: "high",
      note: "Winter month logic: protect water, livestock, and tender starts.",
    };
  }

  if ([2, 3, 9, 10].includes(month)) {
    return {
      label: "Watch",
      tone: "watch",
      note: "Shoulder-season month logic: check local lows before planting.",
    };
  }

  return {
    label: "Low",
    tone: "low",
    note: "Warm-season month logic: frost is unlikely in most local spots.",
  };
};

const getMoonPhase = (date) => {
  const knownNewMoon = new Date(Date.UTC(2000, 0, 6, 18, 14));
  const lunarCycleDays = 29.53058867;
  const daysSince = (date.getTime() - knownNewMoon.getTime()) / 86400000;
  const cyclePosition = ((daysSince % lunarCycleDays) + lunarCycleDays) % lunarCycleDays;
  const phaseIndex = Math.floor((cyclePosition / lunarCycleDays) * 8 + 0.5) % 8;

  return [
    { label: "New Moon", icon: "🌑", note: "A quiet reset for planning and bed prep." },
    { label: "Waxing Crescent", icon: "🌒", note: "Good time to start watching germination." },
    { label: "First Quarter", icon: "🌓", note: "A steady checkpoint for active field work." },
    { label: "Waxing Gibbous", icon: "🌔", note: "Scout crops before the full moon stretch." },
    { label: "Full Moon", icon: "🌕", note: "Bright nights, good for evening checks." },
    { label: "Waning Gibbous", icon: "🌖", note: "Review what is ready to harvest or list." },
    { label: "Last Quarter", icon: "🌗", note: "Useful timing for cleanup and maintenance." },
    { label: "Waning Crescent", icon: "🌘", note: "A good window to reset rows and plans." },
  ][phaseIndex];
};

const hasUsableCoordinate = (value) => Number.isFinite(Number(value));

const getLocationLabel = (userLocation, userProfile) => {
  const profileLocation = userProfile?.location || {};
  const locationText =
    userLocation?.locationText ||
    [userLocation?.city, userLocation?.state].filter(Boolean).join(", ") ||
    userLocation?.county ||
    profileLocation.locationText ||
    [profileLocation.city, profileLocation.state].filter(Boolean).join(", ") ||
    userProfile?.locationText ||
    [userProfile?.city, userProfile?.state].filter(Boolean).join(", ");

  return locationText || FALLBACK_LOCATION.label;
};

const getLocationDetails = (userLocation, userProfile) => {
  const profileLocation = userProfile?.location || {};
  const locationOptions = [
    {
      lat: userLocation?.lat,
      lng: userLocation?.lng,
    },
    {
      lat: profileLocation.lat,
      lng: profileLocation.lng,
    },
    {
      lat: userProfile?.lat,
      lng: userProfile?.lng,
    },
  ];

  const usableLocation = locationOptions.find(
    (location) => hasUsableCoordinate(location.lat) && hasUsableCoordinate(location.lng)
  );

  return {
    lat: usableLocation ? Number(usableLocation.lat) : FALLBACK_LOCATION.lat,
    lng: usableLocation ? Number(usableLocation.lng) : FALLBACK_LOCATION.lng,
    label: getLocationLabel(userLocation, userProfile),
    usingFallback: !usableLocation,
  };
};

const plantingWindowTemplates = [
  {
    key: "cool",
    title: "Cool-season crops",
    icon: "🥬",
    activeMonths: [1, 2, 3, 8, 9, 10],
    accent: "cool",
    bestWindow: "Feb-Apr and Sep-Nov",
    note:
      "Lettuce, spinach, kale, peas, onions, radishes, and brassicas do well when soil is workable and heat is not pushing hard.",
    tasks: ["Watch cold snaps", "Use row cover", "Succession sow greens"],
  },
  {
    key: "warm",
    title: "Warm-season crops",
    icon: "🍅",
    activeMonths: [3, 4, 5, 6, 7],
    accent: "warm",
    bestWindow: "Apr-Aug",
    note:
      "Tomatoes, peppers, beans, squash, okra, corn, and melons want warmer soil and settled nights before they really take off.",
    tasks: ["Harden off starts", "Mulch after soil warms", "Check irrigation"],
  },
  {
    key: "hay",
    title: "Pasture & hay",
    icon: "🌾",
    activeMonths: [2, 3, 4, 7, 8, 9],
    accent: "hay",
    bestWindow: "Mar-May and Aug-Oct",
    note:
      "Scout forage stands, plan fertility from soil tests, manage wet access, and schedule hay work around the cleanest dry stretch.",
    tasks: ["Soil test", "Repair thin stands", "Plan dry hay windows"],
  },
  {
    key: "flowers",
    title: "Flowers & pollinators",
    icon: "🌻",
    activeMonths: [2, 3, 4, 5, 8, 9],
    accent: "flower",
    bestWindow: "Mar-Jun and Sep-Oct",
    note:
      "Pollinator strips, zinnias, sunflowers, herbs, and native blooms support gardens, farm stands, and wildlife corridors.",
    tasks: ["Stagger blooms", "Leave habitat edges", "Water new seedings"],
  },
];

const getPlantingWindows = (month) =>
  plantingWindowTemplates.map((window) => ({
    ...window,
    relevance: window.activeMonths.includes(month) ? "Most relevant now" : "Plan ahead",
    active: window.activeMonths.includes(month),
  }));

const calendarTabs = [
  { key: "this-month", label: "This Month" },
  { key: "spring", label: "Spring" },
  { key: "summer", label: "Summer" },
  { key: "fall", label: "Fall" },
  { key: "winter", label: "Winter" },
  { key: "all", label: "All Crops" },
];

const activityFilters = [
  { key: "all", label: "All activities" },
  { key: "plant-now", label: "Plant now" },
  { key: "start-indoors", label: "Start indoors" },
  { key: "direct-sow", label: "Direct sow" },
  { key: "transplant", label: "Transplant" },
  { key: "harvest", label: "Harvest" },
  { key: "prep", label: "Prep / maintain" },
];

const seasonMonths = {
  spring: [2, 3, 4],
  summer: [5, 6, 7],
  fall: [8, 9, 10],
  winter: [11, 0, 1],
};

const plantingCalendarCrops = [
  {
    crop: "Lettuce",
    activity: "Direct sow",
    activityKey: "direct-sow",
    bestWindow: "Feb-Apr, Aug-Oct",
    harvestWindow: "Apr-Jun, Oct-Nov",
    months: [1, 2, 3, 7, 8, 9],
    harvestMonths: [3, 4, 5, 9, 10],
    notes: "Succession sow small patches and use shade cloth as heat builds.",
    frostSensitivity: "semi-hardy",
    group: "cool",
  },
  {
    crop: "Spinach",
    activity: "Direct sow",
    activityKey: "direct-sow",
    bestWindow: "Feb-Mar, Sep-Oct",
    harvestWindow: "Apr-May, Oct-Nov",
    months: [1, 2, 8, 9],
    harvestMonths: [3, 4, 9, 10],
    notes: "Best in cool soil; germination drops in hot weather.",
    frostSensitivity: "hardy",
    group: "cool",
  },
  {
    crop: "Kale",
    activity: "Plant now",
    activityKey: "plant-now",
    bestWindow: "Feb-Apr, Aug-Oct",
    harvestWindow: "Apr-Dec",
    months: [1, 2, 3, 7, 8, 9],
    harvestMonths: [3, 4, 5, 9, 10, 11],
    notes: "Hardy greens sweeten after cool nights and tolerate light frost.",
    frostSensitivity: "hardy",
    group: "cool",
  },
  {
    crop: "Collards",
    activity: "Transplant",
    activityKey: "transplant",
    bestWindow: "Mar-Apr, Aug-Sep",
    harvestWindow: "May-Jun, Oct-Dec",
    months: [2, 3, 7, 8],
    harvestMonths: [4, 5, 9, 10, 11],
    notes: "Set sturdy starts and keep watered through fall establishment.",
    frostSensitivity: "hardy",
    group: "cool",
  },
  {
    crop: "Cabbage",
    activity: "Transplant",
    activityKey: "transplant",
    bestWindow: "Mar-Apr, Aug",
    harvestWindow: "May-Jun, Oct-Nov",
    months: [2, 3, 7],
    harvestMonths: [4, 5, 9, 10],
    notes: "Watch cabbage worms and keep heads growing steadily.",
    frostSensitivity: "semi-hardy",
    group: "cool",
  },
  {
    crop: "Broccoli",
    activity: "Transplant",
    activityKey: "transplant",
    bestWindow: "Mar-Apr, Aug",
    harvestWindow: "May-Jun, Oct-Nov",
    months: [2, 3, 7],
    harvestMonths: [4, 5, 9, 10],
    notes: "Fall crops often perform better than late spring crops in Tennessee heat.",
    frostSensitivity: "semi-hardy",
    group: "cool",
  },
  {
    crop: "Peas",
    activity: "Direct sow",
    activityKey: "direct-sow",
    bestWindow: "Feb-Mar",
    harvestWindow: "Apr-May",
    months: [1, 2],
    harvestMonths: [3, 4],
    notes: "Sow as soon as soil can be worked; trellis early.",
    frostSensitivity: "hardy",
    group: "cool",
  },
  {
    crop: "Carrots",
    activity: "Direct sow",
    activityKey: "direct-sow",
    bestWindow: "Feb-Apr, Aug-Sep",
    harvestWindow: "May-Jun, Oct-Dec",
    months: [1, 2, 3, 7, 8],
    harvestMonths: [4, 5, 9, 10, 11],
    notes: "Keep seedbed evenly moist until germination.",
    frostSensitivity: "hardy",
    group: "cool",
  },
  {
    crop: "Radishes",
    activity: "Direct sow",
    activityKey: "direct-sow",
    bestWindow: "Feb-Apr, Sep-Oct",
    harvestWindow: "Mar-May, Oct-Nov",
    months: [1, 2, 3, 8, 9],
    harvestMonths: [2, 3, 4, 9, 10],
    notes: "Fast crop for testing bed readiness and market bundles.",
    frostSensitivity: "hardy",
    group: "cool",
  },
  {
    crop: "Beets",
    activity: "Direct sow",
    activityKey: "direct-sow",
    bestWindow: "Mar-Apr, Aug-Sep",
    harvestWindow: "May-Jun, Oct-Nov",
    months: [2, 3, 7, 8],
    harvestMonths: [4, 5, 9, 10],
    notes: "Thin early for better roots and use greens while young.",
    frostSensitivity: "semi-hardy",
    group: "cool",
  },
  {
    crop: "Turnips",
    activity: "Direct sow",
    activityKey: "direct-sow",
    bestWindow: "Mar-Apr, Aug-Oct",
    harvestWindow: "May-Jun, Oct-Dec",
    months: [2, 3, 7, 8, 9],
    harvestMonths: [4, 5, 9, 10, 11],
    notes: "Good dual-purpose root and green crop for fall.",
    frostSensitivity: "hardy",
    group: "cool",
  },
  {
    crop: "Onions",
    activity: "Plant now",
    activityKey: "plant-now",
    bestWindow: "Feb-Mar",
    harvestWindow: "Jun-Jul",
    months: [1, 2],
    harvestMonths: [5, 6],
    notes: "Use sets or transplants; keep weeds down early.",
    frostSensitivity: "semi-hardy",
    group: "cool",
  },
  {
    crop: "Potatoes",
    activity: "Plant now",
    activityKey: "plant-now",
    bestWindow: "Mar-Apr",
    harvestWindow: "Jun-Jul",
    months: [2, 3],
    harvestMonths: [5, 6],
    notes: "Plant seed pieces after soil dries enough to avoid rot.",
    frostSensitivity: "semi-hardy",
    group: "cool",
  },
  {
    crop: "Tomatoes",
    activity: "Transplant",
    activityKey: "transplant",
    bestWindow: "Apr-May",
    harvestWindow: "Jun-Sep",
    months: [3, 4],
    harvestMonths: [5, 6, 7, 8],
    notes: "Wait for settled warm nights; protect from late frost.",
    frostSensitivity: "tender",
    group: "warm",
  },
  {
    crop: "Peppers",
    activity: "Transplant",
    activityKey: "transplant",
    bestWindow: "Apr-May",
    harvestWindow: "Jul-Oct",
    months: [3, 4],
    harvestMonths: [6, 7, 8, 9],
    notes: "Peppers like warmer soil than tomatoes and steady moisture.",
    frostSensitivity: "tender",
    group: "warm",
  },
  {
    crop: "Sweet corn",
    activity: "Direct sow",
    activityKey: "direct-sow",
    bestWindow: "Apr-Jun",
    harvestWindow: "Jul-Sep",
    months: [3, 4, 5],
    harvestMonths: [6, 7, 8],
    notes: "Plant in blocks for pollination and stagger plantings.",
    frostSensitivity: "tender",
    group: "warm",
  },
  {
    crop: "Beans",
    activity: "Direct sow",
    activityKey: "direct-sow",
    bestWindow: "Apr-Jul",
    harvestWindow: "Jun-Oct",
    months: [3, 4, 5, 6],
    harvestMonths: [5, 6, 7, 8, 9],
    notes: "Sow after frost and avoid working wet foliage.",
    frostSensitivity: "tender",
    group: "warm",
  },
  {
    crop: "Cucumbers",
    activity: "Direct sow",
    activityKey: "direct-sow",
    bestWindow: "Apr-Jun",
    harvestWindow: "Jun-Sep",
    months: [3, 4, 5],
    harvestMonths: [5, 6, 7, 8],
    notes: "Use trellis or mulch to improve airflow and picking.",
    frostSensitivity: "tender",
    group: "warm",
  },
  {
    crop: "Squash",
    activity: "Direct sow",
    activityKey: "direct-sow",
    bestWindow: "Apr-Jul",
    harvestWindow: "Jun-Oct",
    months: [3, 4, 5, 6],
    harvestMonths: [5, 6, 7, 8, 9],
    notes: "Scout squash bugs early and harvest young summer squash often.",
    frostSensitivity: "tender",
    group: "warm",
  },
  {
    crop: "Okra",
    activity: "Direct sow",
    activityKey: "direct-sow",
    bestWindow: "May-Jun",
    harvestWindow: "Jul-Sep",
    months: [4, 5],
    harvestMonths: [6, 7, 8],
    notes: "Needs heat; do not rush into cool soil.",
    frostSensitivity: "tender",
    group: "warm",
  },
  {
    crop: "Melons",
    activity: "Direct sow",
    activityKey: "direct-sow",
    bestWindow: "May-Jun",
    harvestWindow: "Jul-Sep",
    months: [4, 5],
    harvestMonths: [6, 7, 8],
    notes: "Give vines room, mulch well, and wait for warm soil.",
    frostSensitivity: "tender",
    group: "warm",
  },
  {
    crop: "Pumpkins",
    activity: "Direct sow",
    activityKey: "direct-sow",
    bestWindow: "May-Jul",
    harvestWindow: "Sep-Oct",
    months: [4, 5, 6],
    harvestMonths: [8, 9],
    notes: "Time plantings for fall sales and watch vine pressure.",
    frostSensitivity: "tender",
    group: "warm",
  },
  {
    crop: "Garlic",
    activity: "Plant now",
    activityKey: "plant-now",
    bestWindow: "Oct-Nov",
    harvestWindow: "Jun-Jul",
    months: [9, 10],
    harvestMonths: [5, 6],
    notes: "Plant cloves before hard winter; mulch after planting.",
    frostSensitivity: "hardy",
    group: "fall",
  },
  {
    crop: "Cover crops",
    activity: "Prep / maintain",
    activityKey: "prep",
    bestWindow: "Aug-Nov, Feb-Mar",
    harvestWindow: "Terminate before planting",
    months: [7, 8, 9, 10, 1, 2],
    harvestMonths: [2, 3, 4],
    notes: "Use rye, clover, peas, oats, or mixes to hold soil and feed beds.",
    frostSensitivity: "hardy",
    group: "soil",
  },
  {
    crop: "Strawberries",
    activity: "Plant now",
    activityKey: "plant-now",
    bestWindow: "Sep-Oct, Mar-Apr",
    harvestWindow: "Apr-Jun",
    months: [8, 9, 2, 3],
    harvestMonths: [3, 4, 5],
    notes: "Keep crowns at soil line and mulch before winter.",
    frostSensitivity: "semi-hardy",
    group: "fall",
  },
  {
    crop: "Herbs",
    activity: "Start indoors",
    activityKey: "start-indoors",
    bestWindow: "Feb-Apr",
    harvestWindow: "May-Oct",
    months: [1, 2, 3],
    harvestMonths: [4, 5, 6, 7, 8, 9],
    notes: "Start basil warm; parsley, cilantro, dill prefer cooler windows.",
    frostSensitivity: "semi-hardy",
    group: "herbs",
  },
];

const monthMatches = (months, month) => Array.isArray(months) && months.includes(month);

const tabMatchesCrop = (crop, tab, month) => {
  if (tab === "all") return true;
  if (tab === "this-month") {
    return monthMatches(crop.months, month) || monthMatches(crop.harvestMonths, month);
  }
  const months = seasonMonths[tab] || [];
  return (
    crop.months.some((cropMonth) => months.includes(cropMonth)) ||
    crop.harvestMonths.some((cropMonth) => months.includes(cropMonth))
  );
};

const cropVisuals = {
  lettuce: { icon: "🥬", type: "Leafy greens", typeKey: "leafy", careTip: "Harvest outside leaves young for better regrowth." },
  spinach: { icon: "🥬", type: "Leafy greens", typeKey: "leafy", careTip: "Keep seedbeds cool and evenly moist." },
  kale: { icon: "🥬", type: "Brassicas", typeKey: "brassica", careTip: "Pick lower leaves and keep plants growing." },
  collards: { icon: "🥬", type: "Brassicas", typeKey: "brassica", careTip: "Steady moisture keeps leaves tender." },
  cabbage: { icon: "🥬", type: "Brassicas", typeKey: "brassica", careTip: "Watch worms before heads tighten." },
  broccoli: { icon: "🥦", type: "Brassicas", typeKey: "brassica", careTip: "Harvest main heads before buds open." },
  peas: { icon: "🌱", type: "Legumes", typeKey: "legume", careTip: "Add trellis early before vines tangle." },
  carrots: { icon: "🥕", type: "Roots", typeKey: "root", careTip: "Thin early and avoid crusted soil." },
  radishes: { icon: "🌱", type: "Roots", typeKey: "root", careTip: "Pull promptly for crisp roots." },
  beets: { icon: "🌱", type: "Roots", typeKey: "root", careTip: "Use young greens while roots size up." },
  turnips: { icon: "🌱", type: "Roots", typeKey: "root", careTip: "Harvest small for sweeter roots." },
  onions: { icon: "🧅", type: "Roots", typeKey: "root", careTip: "Keep weeds out while bulbs form." },
  potatoes: { icon: "🥔", type: "Roots", typeKey: "root", careTip: "Hill soil as plants grow." },
  tomatoes: { icon: "🍅", type: "Fruiting crops", typeKey: "fruiting", careTip: "Mulch, stake, and protect from late cold." },
  peppers: { icon: "🫑", type: "Fruiting crops", typeKey: "fruiting", careTip: "Wait for warm soil before pushing growth." },
  "sweet corn": { icon: "🌽", type: "Fruiting crops", typeKey: "fruiting", careTip: "Plant in blocks for better pollination." },
  beans: { icon: "🌱", type: "Legumes", typeKey: "legume", careTip: "Avoid picking when foliage is wet." },
  cucumbers: { icon: "🥒", type: "Fruiting crops", typeKey: "fruiting", careTip: "Trellis for airflow and easier harvest." },
  squash: { icon: "🌼", type: "Fruiting crops", typeKey: "fruiting", careTip: "Scout for squash bugs early." },
  okra: { icon: "🌿", type: "Fruiting crops", typeKey: "fruiting", careTip: "Pick pods young and often." },
  melons: { icon: "🍉", type: "Fruiting crops", typeKey: "fruiting", careTip: "Give vines room and consistent moisture." },
  pumpkins: { icon: "🎃", type: "Fruiting crops", typeKey: "fruiting", careTip: "Time vines for fall demand." },
  garlic: { icon: "🧄", type: "Roots", typeKey: "root", careTip: "Mulch after planting for winter protection." },
  "cover crops": { icon: "🌾", type: "Soil/cover crops", typeKey: "soil", careTip: "Terminate before the next crop needs the bed." },
  strawberries: { icon: "🍓", type: "Fruiting crops", typeKey: "fruiting", careTip: "Keep crowns high and mulch clean." },
  herbs: { icon: "🌿", type: "Herbs", typeKey: "herb", careTip: "Pinch tips to encourage branching." },
};

const groupSeasonTone = {
  cool: "spring",
  warm: "summer",
  fall: "fall",
  soil: "neutral",
  herbs: "spring",
};

const monthFocusCopy = [
  "January focus: plan seed orders, check stored feed, and prep early crop maps.",
  "February focus: start cool-season crops, soil test, and watch wet access.",
  "March focus: direct sow hardy crops and keep frost covers ready.",
  "April focus: transplant cool crops, harden starts, and watch late frost.",
  "May focus: warm-season crops, transplanting, and watching wet fields.",
  "June focus: harvest early, keep water steady, and plan hay windows.",
  "July focus: heat care, irrigation, pest scouting, and succession planting.",
  "August focus: start fall crops, prep beds, and watch late-summer storms.",
  "September focus: fall greens, cover crops, and market-season refreshes.",
  "October focus: garlic, cover crops, frost protection, and field cleanup.",
  "November focus: winter prep, soil cover, stored goods, and equipment care.",
  "December focus: plan next season, protect water systems, and review records.",
];

const getCropVisual = (crop) => {
  const key = crop.crop.toLowerCase();
  return (
    cropVisuals[key] || {
      icon: "🌱",
      type: "Farm crop",
      typeKey: "neutral",
      careTip: "Match planting to soil temperature and the local forecast.",
    }
  );
};

const getCalendarCropScore = (crop, month, calendarActivity, originalIndex) => {
  const plantingNow = monthMatches(crop.months, month);
  const harvestingNow = monthMatches(crop.harvestMonths, month);
  const warmMonth = [3, 4, 5, 6].includes(month);
  const coolMonth = [1, 2, 3, 8, 9, 10].includes(month);
  const plantingActivityKeys = ["plant-now", "direct-sow", "transplant", "start-indoors"];
  let score = 0;

  if (plantingNow) score += 80;
  if (harvestingNow) score += 36;
  if (warmMonth && crop.group === "warm") score += 34;
  if (coolMonth && ["cool", "fall", "herbs"].includes(crop.group)) score += 20;
  if (calendarActivity === "harvest" && harvestingNow) score += 32;
  if (
    calendarActivity === "plant-now" &&
    plantingNow &&
    plantingActivityKeys.includes(crop.activityKey)
  ) {
    score += 30;
  }
  if (calendarActivity !== "all" && crop.activityKey === calendarActivity && plantingNow) {
    score += 28;
  }
  if (crop.activityKey === "prep" && calendarActivity !== "prep") score -= 32;
  if (!plantingNow && !harvestingNow) score -= 20;

  return score - originalIndex * 0.01;
};

const reminderBank = {
  0: [
    "Review equipment maintenance before spring work stacks up.",
    "Check stored hay quality and ventilation after damp weather.",
    "Plan seed orders and map early planting beds.",
    "List extra winter eggs, firewood, or stored goods in the marketplace.",
  ],
  1: [
    "Soil test before heavy planting and pasture fertilizing.",
    "Service gates, waterers, and fencing ahead of spring turnout.",
    "Start cool-season transplants where protected space is available.",
    "Post day jobs early for pruning, cleanup, and fence repairs.",
  ],
  2: [
    "Check fencing before spring turnout.",
    "Watch wet field access after heavy rain.",
    "Start direct sowing hardy cool-season crops as soil allows.",
    "Add local events early so nearby users can find them.",
  ],
  3: [
    "Keep frost cloth ready for tender starts and early blooms.",
    "Soil test before heavy planting if you have not already.",
    "Post day jobs before busy weekends.",
    "List extra produce, eggs, starts, or compost in the marketplace.",
  ],
  4: [
    "Harden off warm-season transplants before setting them out.",
    "Scout for weeds while they are still small.",
    "Confirm hay equipment, twine, wrap, and storage plans.",
    "Ask the community board about local pest pressure and varieties.",
  ],
  5: [
    "Harvest early and cool produce quickly on hot days.",
    "Watch livestock shade, water, and heat stress.",
    "List seasonal products while buyers are planning weekend trips.",
    "Keep hay windows flexible around scattered storms.",
  ],
  6: [
    "Irrigate deeply when rainfall gets spotty.",
    "Scout squash bugs, hornworms, and pasture stress.",
    "Post farm help requests early in the week for weekend jobs.",
    "Refresh marketplace listings with current photos and quantities.",
  ],
  7: [
    "Start planning fall garden beds and cool-season seedings.",
    "Renovate thin pasture after rain returns.",
    "Check fencing and gates before late-summer rotations.",
    "Add fall events early so nearby users can find them.",
  ],
  8: [
    "Plant fall greens, roots, and cover crops as weather opens.",
    "Monitor late heat while establishing cool-season crops.",
    "List pumpkins, mums, hay, eggs, and seasonal produce.",
    "Ask the community board for local frost-date observations.",
  ],
  9: [
    "Watch local frost and cover tender crops when needed.",
    "Move wet-weather chores ahead of colder fronts.",
    "Clean up spent rows and seed cover crops where practical.",
    "Post day jobs before busy harvest and event weekends.",
  ],
  10: [
    "Winterize hoses, pumps, and exposed water lines.",
    "Check stored feed, bedding, and hay inventory.",
    "List extra produce, eggs, firewood, or handmade goods.",
    "Plan holiday market posts and local farm events.",
  ],
  11: [
    "Protect water sources during hard freezes.",
    "Walk fences after storms and wind.",
    "Review records from the season while details are fresh.",
    "Use the community board for local repair, feed, and winter advice.",
  ],
};

const getSeasonalReminders = (month) => reminderBank[month] || reminderBank[0];

const tipsBank = {
  0: [
    "Use January downtime to refresh profiles, services, and product photos.",
    "Post winter repair jobs with flexible dates so local help can plan ahead.",
    "List stored hay, eggs, firewood, and handmade goods before cold weekends.",
    "Ask the community board for local winter water and livestock tips.",
    "Add late-winter classes or markets early so people can save the date.",
  ],
  1: [
    "Post pruning, cleanup, and fence jobs before spring calendars fill.",
    "List early starts, eggs, compost, hay, and farm supplies as planning begins.",
    "Watch local lows before planting cool-season crops outside.",
    "Ask neighbors what varieties handled last year's weather best.",
    "Add March and April events before weekend traffic picks up.",
  ],
  2: [
    "Post farm help early in the week for weekend turnout, fencing, and cleanup.",
    "List eggs, starts, compost, and early greens as local buyers start looking.",
    "Check frost and wet field access before planting or hauling.",
    "Use the community board for pest timing and soil prep advice.",
    "Add spring markets and farm days early.",
  ],
  3: [
    "Post planting and mowing jobs before the first pretty weekend.",
    "List extra starts, eggs, greens, and compost while demand is high.",
    "Keep frost cloth handy until local lows settle.",
    "Ask the community which warm-season crops are safe to set out nearby.",
    "Add vendor opportunities before planners book their weekends.",
  ],
  4: [
    "Post hay, planting, fencing, and weekend help requests before Friday.",
    "List starts, flowers, eggs, greens, and early produce with fresh photos.",
    "Check rain and field access before moving equipment.",
    "Ask the community about local pest pressure before spraying or planting.",
    "Add summer events early so nearby users can find them.",
  ],
  5: [
    "Post help requests early for harvest, hay, and market prep.",
    "List produce before peak pickup windows, especially Thursday to Saturday.",
    "Check heat, wind, and rain before hay work or livestock moves.",
    "Use the community board for irrigation and pest observations.",
    "Add farm events before summer weekends get crowded.",
  ],
  6: [
    "Post farm help before heat waves so work can start early.",
    "List seasonal products with clear pickup windows and current quantities.",
    "Check heat index and rainfall before planting second successions.",
    "Ask the community about pest outbreaks and local repair leads.",
    "Add late-summer events and vendor spots ahead of time.",
  ],
  7: [
    "Post pasture, fence, and fall bed prep jobs before school-year schedules tighten.",
    "List late summer produce, hay, eggs, and flowers before weekend pickup.",
    "Check rain chances before fall seedings.",
    "Use community advice for fall varieties and pasture renovation timing.",
    "Add September and October events early.",
  ],
  8: [
    "Post help requests for harvest, markets, and fall cleanup by midweek.",
    "List pumpkins, mums, hay, eggs, and fall produce while shoppers are planning.",
    "Watch first-frost chatter before setting late plantings.",
    "Ask the community for local frost pockets and cover crop timing.",
    "Add fall farm events with plenty of notice.",
  ],
  9: [
    "Post harvest, cleanup, and event jobs before cool weekends.",
    "List fall produce, eggs, firewood, hay, and handmade goods.",
    "Check frost and wind before leaving tender crops uncovered.",
    "Use community posts for local frost reports and equipment needs.",
    "Add holiday markets and late-fall events early.",
  ],
  10: [
    "Post winter prep, hauling, and repair jobs before weather turns.",
    "List storage crops, eggs, firewood, hay, and handmade gifts.",
    "Check freezing nights before water-line and livestock chores.",
    "Ask the community for local hay, feed, and repair leads.",
    "Add winter events before holiday calendars fill.",
  ],
  11: [
    "Post repair and winter help jobs with clear weather windows.",
    "List eggs, firewood, hay, handmade goods, and holiday farm products.",
    "Check freezing rain and wind before travel or livestock moves.",
    "Use the community board for winter advice and trusted local help.",
    "Add January events and classes before year-end distractions.",
  ],
};

const getAlmanacTips = (month) => tipsBank[month] || tipsBank[0];

const getFieldCondition = (todayPrecip, currentPrecip, currentRain) => {
  const precipitation = Math.max(
    Number(todayPrecip) || 0,
    Number(currentPrecip) || 0,
    Number(currentRain) || 0
  );

  if (precipitation < 0.03) {
    return {
      label: "Dry",
      tone: "dry",
      note: "Little precipitation so far. Dust and irrigation may matter.",
    };
  }

  if (precipitation < 0.25) {
    return {
      label: "Damp",
      tone: "damp",
      note: "Light moisture. Check gateways and low spots before equipment work.",
    };
  }

  return {
    label: "Wet/Muddy",
    tone: "wet",
    note: "Moderate or heavy moisture. Protect soil structure and access lanes.",
  };
};

const getWindNote = (windSpeed, gusts) => {
  const wind = Number(windSpeed) || 0;
  const gust = Number(gusts) || 0;

  if (gust >= 35 || wind >= 25) return "Very windy: delay spraying, covers, and light hauling.";
  if (gust >= 25 || wind >= 16) return "Breezy: secure covers, gates, and loose materials.";
  if (wind >= 8) return "Light to moderate breeze for most field chores.";
  return "Calm wind for careful planting, covers, and market setup.";
};

const formatTemp = (value) => (Number.isFinite(Number(value)) ? `${Math.round(Number(value))}°F` : "--");
const formatPercent = (value) => (Number.isFinite(Number(value)) ? `${Math.round(Number(value))}%` : "--");
const formatWind = (value) => (Number.isFinite(Number(value)) ? `${Math.round(Number(value))} mph` : "--");
const getDayLabel = (dateString, index) => {
  if (index === 0) return "Today";
  if (index === 1) return "Tomorrow";

  const date = new Date(`${dateString}T12:00:00`);
  return date.toLocaleDateString(undefined, { weekday: "short" });
};

const buildForecastUrl = ({ lat, lng }) => {
  const params = new URLSearchParams({
    latitude: String(lat),
    longitude: String(lng),
    current:
      "temperature_2m,apparent_temperature,is_day,precipitation,rain,weather_code,wind_speed_10m,wind_gusts_10m",
    hourly:
      "temperature_2m,precipitation_probability,precipitation,wind_speed_10m,soil_temperature_0cm,soil_moisture_0_to_1cm",
    daily:
      "temperature_2m_max,temperature_2m_min,precipitation_sum,precipitation_probability_max,wind_speed_10m_max,sunrise,sunset",
    temperature_unit: "fahrenheit",
    wind_speed_unit: "mph",
    precipitation_unit: "inch",
    forecast_days: "7",
    timezone: "auto",
  });

  return `https://api.open-meteo.com/v1/forecast?${params.toString()}`;
};

const actionLinks = [
  {
    title: "Post a Job",
    icon: "🛠",
    description: "Line up help for field work, fencing, cleanup, hauling, and seasonal chores.",
    to: "/map",
    tone: "job",
  },
  {
    title: "Browse Local Products",
    icon: "🛒",
    description: "Find produce, eggs, hay, handmade goods, and farm stand listings nearby.",
    to: "/home",
    state: { dashboardView: "market" },
    tone: "market",
  },
  {
    title: "Add an Event",
    icon: "📅",
    description: "Share farm days, vendor spots, markets, classes, and rural community events.",
    to: "/add-event",
    tone: "event",
  },
  {
    title: "Ask the Community",
    icon: "💬",
    description: "Get local advice on planting timing, pests, weather, repairs, and supplies.",
    to: "/community",
    tone: "community",
  },
];

const AlmanacPage = ({ userLocation, userProfile }) => {
  const locationDetails = useMemo(
    () => getLocationDetails(userLocation, userProfile),
    [userLocation, userProfile]
  );
  const [weatherState, setWeatherState] = useState({
    data: null,
    error: false,
    fetchedAt: null,
    loading: true,
  });
  const [calendarTab, setCalendarTab] = useState("this-month");
  const [calendarActivity, setCalendarActivity] = useState("all");
  const [calendarVisibleCount, setCalendarVisibleCount] = useState(6);

  const fetchWeather = useCallback(
    async (signal) => {
      setWeatherState((currentState) => ({
        ...currentState,
        error: false,
        loading: true,
      }));

      try {
        const response = await fetch(buildForecastUrl(locationDetails), { signal });

        if (!response.ok) {
          throw new Error("Weather unavailable");
        }

        const data = await response.json();
        setWeatherState({
          data,
          error: false,
          fetchedAt: new Date(),
          loading: false,
        });
      } catch (error) {
        if (error.name === "AbortError") return;

        setWeatherState((currentState) => ({
          ...currentState,
          error: true,
          loading: false,
        }));
      }
    },
    [locationDetails.lat, locationDetails.lng]
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchWeather(controller.signal);
    return () => controller.abort();
  }, [fetchWeather]);

  useEffect(() => {
    setCalendarVisibleCount(6);
  }, [calendarActivity, calendarTab]);

  const handleRefreshWeather = () => {
    const controller = new AbortController();
    fetchWeather(controller.signal);
  };

  const almanacData = useMemo(() => {
    const today = new Date();
    const month = today.getMonth();
    const season = getSeason(month);
    const moonPhase = getMoonPhase(today);
    const current = weatherState.data?.current || {};
    const daily = weatherState.data?.daily || {};
    const hourly = weatherState.data?.hourly || {};
    const currentTemp = Number(current.temperature_2m);
    const apparentTemp = Number(current.apparent_temperature);
    const dailyLow = Number(daily.temperature_2m_min?.[0]);
    const dailyHigh = Number(daily.temperature_2m_max?.[0]);
    const todayPrecip = Number(daily.precipitation_sum?.[0]);
    const rainChance = Number(daily.precipitation_probability_max?.[0]);
    const currentWind = Number(current.wind_speed_10m);
    const currentGusts = Number(current.wind_gusts_10m);
    const weatherCode = Number(current.weather_code);
    const frostRisk = getFrostRisk(month, currentTemp, dailyLow);
    const fieldCondition = getFieldCondition(
      todayPrecip,
      current.precipitation,
      current.rain
    );
    const weatherLabel = Number.isFinite(weatherCode)
      ? getWeatherCodeLabel(weatherCode)
      : weatherState.error
      ? "Weather guidance"
      : "Weather update";
    const weatherIcon = getWeatherIcon(weatherCode, rainChance);
    const firstSoilTemp = hourly.soil_temperature_0cm?.find((value) =>
      Number.isFinite(Number(value))
    );
    const firstSoilMoisture = hourly.soil_moisture_0_to_1cm?.find((value) =>
      Number.isFinite(Number(value))
    );

    const forecastDays = (daily.time || []).slice(0, 7).map((dateString, index) => ({
      date: dateString,
      label: getDayLabel(dateString, index),
      high: daily.temperature_2m_max?.[index],
      low: daily.temperature_2m_min?.[index],
      precip: daily.precipitation_sum?.[index],
      rainChance: daily.precipitation_probability_max?.[index],
      wind: daily.wind_speed_10m_max?.[index],
      weatherLabel: getForecastWeatherLabel(
        weatherCode,
        daily.precipitation_probability_max?.[index],
        index
      ),
      weatherIcon: getWeatherIcon(
        index === 0 ? weatherCode : undefined,
        daily.precipitation_probability_max?.[index]
      ),
      sunrise: daily.sunrise?.[index],
      sunset: daily.sunset?.[index],
    }));

    return {
      today,
      month,
      monthName: MONTH_NAMES[month],
      dateLabel: today.toLocaleDateString(undefined, {
        weekday: "long",
        month: "long",
        day: "numeric",
        year: "numeric",
      }),
      shortDateLabel: today.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
      }),
      season,
      moonPhase,
      frostRisk,
      fieldCondition,
      weatherLabel,
      weatherIcon,
      weatherCode,
      currentTemp,
      apparentTemp,
      dailyHigh,
      dailyLow,
      todayPrecip,
      rainChance,
      currentWind,
      currentGusts,
      windNote: getWindNote(currentWind, currentGusts),
      soilTemp: firstSoilTemp,
      soilMoisture: firstSoilMoisture,
      plantingWindows: getPlantingWindows(month),
      reminders: getSeasonalReminders(month),
      tips: getAlmanacTips(month),
      forecastDays,
      sunrise: daily.sunrise?.[0],
      sunset: daily.sunset?.[0],
    };
  }, [weatherState.data, weatherState.error]);

  const plantingCalendar = useMemo(() => {
    const currentMonth = almanacData.month;
    const activeTabMonths =
      calendarTab === "this-month"
        ? [currentMonth]
        : calendarTab === "all"
        ? null
        : seasonMonths[calendarTab] || [];
    const plantingActivityKeys = ["plant-now", "direct-sow", "transplant", "start-indoors"];
    const withRelevance = plantingCalendarCrops.map((crop, index) => {
      const visual = getCropVisual(crop);
      const plantingNow = monthMatches(crop.months, currentMonth);
      const harvestingNow = monthMatches(crop.harvestMonths, currentMonth);
      const relevantForActivity =
        calendarActivity === "all"
          ? plantingNow || harvestingNow
          : calendarActivity === "harvest"
          ? harvestingNow
          : calendarActivity === "plant-now"
          ? plantingNow && plantingActivityKeys.includes(crop.activityKey)
          : plantingNow && crop.activityKey === calendarActivity;

      return {
        ...crop,
        ...visual,
        activityTone: crop.activityKey,
        frostTone: crop.frostSensitivity,
        seasonTone: groupSeasonTone[crop.group] || "neutral",
        relevantNow: relevantForActivity,
        harvestingNow,
        calendarScore: getCalendarCropScore(
          crop,
          currentMonth,
          calendarActivity,
          index
        ),
      };
    });

    const filteredCrops = withRelevance
      .filter((crop) => tabMatchesCrop(crop, calendarTab, currentMonth))
      .filter((crop) => {
        if (calendarActivity === "all") return true;
        if (calendarActivity === "plant-now") {
          return plantingActivityKeys.includes(crop.activityKey);
        }
        if (calendarActivity === "harvest") {
          if (!activeTabMonths) return crop.harvestMonths.length > 0;
          return crop.harvestMonths.some((cropMonth) =>
            activeTabMonths.includes(cropMonth)
          );
        }
        return crop.activityKey === calendarActivity;
      })
      .sort((a, b) => b.calendarScore - a.calendarScore);

    const monthCrops = withRelevance.filter((crop) => crop.relevantNow);

    return {
      filteredCrops,
      visibleCrops: filteredCrops.slice(0, calendarVisibleCount),
      hasMore: filteredCrops.length > calendarVisibleCount,
      hasFrostWarning:
        ["high", "watch"].includes(almanacData.frostRisk.tone) &&
        filteredCrops
          .slice(0, calendarVisibleCount)
          .some((crop) => crop.frostSensitivity === "tender"),
      currentFocus: monthFocusCopy[currentMonth],
      summary: {
        plantNow: monthCrops.filter((crop) =>
          ["plant-now", "direct-sow", "transplant", "start-indoors"].includes(
            crop.activityKey
          )
        ).length,
        harvestSoon: monthCrops.filter((crop) => crop.harvestingNow).length,
        prep: monthCrops.filter((crop) => crop.activityKey === "prep").length,
      },
    };
  }, [almanacData.frostRisk.tone, almanacData.month, calendarActivity, calendarTab, calendarVisibleCount]);

  const quickNote = weatherState.error
    ? "Live weather is offline, but date-based farm guidance is still ready."
    : almanacData.fieldCondition.tone === "wet"
    ? "Protect wet lanes and low spots before moving equipment."
    : almanacData.frostRisk.tone === "high"
    ? "Freeze protection should be the first field check."
    : "Good day to match chores with the forecast window.";

  const snapshotItems = [
    {
      label: "Date",
      value: almanacData.shortDateLabel,
      detail: almanacData.dateLabel,
      icon: "\uD83D\uDCC5",
      tone: "today",
    },
    {
      label: "Season",
      value: almanacData.season,
      detail: `${almanacData.monthName} field notes`,
      icon: "\uD83C\uDF3E",
      tone: "season",
    },
    {
      label: "Frost Risk",
      value: almanacData.frostRisk.label,
      detail: almanacData.frostRisk.note,
      icon: "\uD83E\uDDCA",
      tone: `frost-${almanacData.frostRisk.tone}`,
    },
    {
      label: "Moon Phase",
      value: almanacData.moonPhase.label,
      detail: almanacData.moonPhase.note,
      icon: almanacData.moonPhase.icon,
      tone: "moon",
    },
    {
      label: "Quick Note",
      value: "Daily cue",
      detail: quickNote,
      icon: "\uD83D\uDCDD",
      tone: `field-${almanacData.fieldCondition.tone}`,
    },
  ];

  const weatherStatusText = weatherState.loading
    ? "Loading live weather"
    : weatherState.error
    ? "Weather could not load"
    : almanacData.weatherLabel;

  return (
    <main className="almanac-page page-shell-wide">
      <section className="almanac-hero" aria-labelledby="almanac-title">
        <div className="almanac-hero-copy">
          <span className="almanac-kicker">
            🌦 Daily guide for {locationDetails.label}
          </span>
          <h1 id="almanac-title">Farm Almanac</h1>
          <p>
            Local growing notes, live weather cues, moon phase, and seasonal
            reminders for your area.
          </p>
          <div className="almanac-hero-meta">
            <span>📍 {locationDetails.label}</span>
            <span>{almanacData.dateLabel}</span>
            <span>{almanacData.season} notes</span>
          </div>
        </div>

        <div className="almanac-scenic-panel" aria-hidden="true">
          <div className="almanac-scenic-sun">☀️</div>
          <div className="almanac-scenic-moon">{almanacData.moonPhase.icon}</div>
          <div className="almanac-scenic-cloud cloud-one">☁️</div>
          <div className="almanac-scenic-cloud cloud-two">☁️</div>
          <div className="almanac-scenic-field">
            <span />
            <span />
            <span />
            <span />
          </div>
          <div className="almanac-scenic-note">
            <strong>{weatherStatusText}</strong>
            <span>{almanacData.frostRisk.label} frost risk</span>
          </div>
        </div>
      </section>

      <section className="almanac-snapshot-section" aria-label="Today snapshot">
        <div className="almanac-section-heading">
          <div>
            <span className="almanac-eyebrow">Today snapshot</span>
            <h2>Live field cues</h2>
          </div>
          <p>Fast local cues for planning chores, listings, and planting decisions.</p>
        </div>

        <div className="almanac-snapshot-grid live">
          {snapshotItems.map((item) => (
            <article className={`almanac-snapshot-card ${item.tone}`} key={item.label}>
              <span className="almanac-card-icon">{item.icon}</span>
              <div>
                <p>{item.label}</p>
                <strong>{item.value}</strong>
                <small>{item.detail}</small>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="almanac-weather-panel live-weather" aria-label="Live weather">
        <div className="almanac-weather-main">
          <span className="almanac-weather-label">{weatherStatusText}</span>
          <h2>{weatherState.error ? "Seasonal Weather Guidance" : "Live Farm Weather"}</h2>
          <p>
            {weatherState.error
              ? "Live weather is unavailable right now, but the Almanac is still using your date, season, moon phase, and local growing logic."
              : "Live weather helps estimate frost risk, rain chance, wind, and field conditions before you plant, haul, cut hay, or post help."}
          </p>

          <div className="almanac-weather-actions">
            <button type="button" className="btn-primary" onClick={handleRefreshWeather}>
              {weatherState.loading ? "Refreshing..." : "Refresh Weather"}
            </button>
            <span>Weather data from Open-Meteo</span>
          </div>
        </div>

        <div className="almanac-weather-dashboard">
          <div className="almanac-weather-current">
            <span className="weather-current-icon">{almanacData.weatherIcon}</span>
            <strong>{weatherState.loading ? "Loading" : formatTemp(almanacData.currentTemp)}</strong>
            <p>{almanacData.weatherLabel}</p>
          </div>

          <div className="almanac-weather-grid">
            <div className="almanac-weather-cell">
              <span>Feels like</span>
              <strong>{formatTemp(almanacData.apparentTemp)}</strong>
            </div>
            <div className="almanac-weather-cell">
              <span>Rain chance</span>
              <strong>{formatPercent(almanacData.rainChance)}</strong>
            </div>
            <div className="almanac-weather-cell">
              <span>Wind</span>
              <strong>{formatWind(almanacData.currentWind)}</strong>
            </div>
            <div className="almanac-weather-cell">
              <span>Gusts</span>
              <strong>{formatWind(almanacData.currentGusts)}</strong>
            </div>
            <div className={`almanac-weather-cell frost-${almanacData.frostRisk.tone}`}>
              <span>Frost risk</span>
              <strong>{almanacData.frostRisk.label}</strong>
            </div>
            <div className={`almanac-weather-cell field-${almanacData.fieldCondition.tone}`}>
              <span>Field condition</span>
              <strong>{almanacData.fieldCondition.label}</strong>
            </div>
          </div>
        </div>
      </section>

      <section className="almanac-forecast-section">
        <div className="almanac-section-heading">
          <div>
            <span className="almanac-eyebrow">7-day outlook</span>
            <h2>Farm Forecast Strip</h2>
          </div>
          <p>Use the week ahead to plan planting windows, hay timing, market posts, and job requests.</p>
        </div>

        {weatherState.loading && almanacData.forecastDays.length === 0 ? (
          <div className="almanac-forecast-loading">Loading 7-day forecast...</div>
        ) : almanacData.forecastDays.length > 0 ? (
          <div className="almanac-forecast-grid">
            {almanacData.forecastDays.map((day) => (
              <article className="almanac-forecast-card" key={day.date}>
                <span>{day.label}</span>
                <div className="forecast-weather-icon" aria-hidden="true">
                  {day.weatherIcon}
                </div>
                <p className="forecast-weather-label">{day.weatherLabel}</p>
                <strong>
                  {formatTemp(day.high)} / {formatTemp(day.low)}
                </strong>
                <p>Rain {formatPercent(day.rainChance)}</p>
              </article>
            ))}
          </div>
        ) : (
          <div className="almanac-forecast-loading">
            Forecast cards will appear when weather data reconnects.
          </div>
        )}
      </section>

      <section className="almanac-section planting-calendar-section">
        <div className="almanac-section-heading">
          <div>
            <span className="almanac-eyebrow">Year-round crop timing</span>
            <h2>Planting &amp; Harvest Calendar</h2>
          </div>
          <p>
            Dates are general guidance for Middle Tennessee and similar growing zones.
            Always adjust for your local frost dates, soil temperature, and weather.
          </p>
        </div>

        <div className="planting-calendar-focus">
          <span>This Month</span>
          <strong>{plantingCalendar.currentFocus}</strong>
        </div>

        <div className="planting-calendar-summary" aria-label="This month crop summary">
          <span className="planting-summary-item plant">
            <strong>{plantingCalendar.summary.plantNow}</strong>
            Plant now
          </span>
          <span className="planting-summary-item harvest">
            <strong>{plantingCalendar.summary.harvestSoon}</strong>
            Harvest soon
          </span>
          <span className="planting-summary-item prep">
            <strong>{plantingCalendar.summary.prep}</strong>
            Prep this month
          </span>
        </div>

        <div className="planting-calendar-controls">
          <div className="planting-calendar-tabs" aria-label="Calendar season filters">
            {calendarTabs.map((tab) => (
              <button
                type="button"
                key={tab.key}
                className={calendarTab === tab.key ? "active" : ""}
                onClick={() => setCalendarTab(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="planting-calendar-activities" aria-label="Calendar activity filters">
            {activityFilters.map((filter) => (
              <button
                type="button"
                key={filter.key}
                className={calendarActivity === filter.key ? "active" : ""}
                onClick={() => setCalendarActivity(filter.key)}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>

        {(plantingCalendar.hasFrostWarning ||
          ["damp", "wet"].includes(almanacData.fieldCondition.tone)) && (
          <div className="planting-calendar-alerts">
            {plantingCalendar.hasFrostWarning && (
              <div className="planting-calendar-warning">
                Protect tender crops tonight.
              </div>
            )}
            {["damp", "wet"].includes(almanacData.fieldCondition.tone) && (
              <div className="planting-calendar-field-note">
                Let low spots firm up before heavy equipment work.
              </div>
            )}
          </div>
        )}

        <div className="planting-calendar-grid">
          {plantingCalendar.visibleCrops.map((crop) => (
            <article
              className={`planting-calendar-card season-${crop.seasonTone} type-${crop.typeKey} ${
                crop.relevantNow ? "is-current" : ""
              }`}
              key={`${crop.crop}-${crop.activity}`}
            >
              <div className="planting-calendar-card-top">
                <div className="planting-calendar-crop-title">
                  <span aria-hidden="true">{crop.icon}</span>
                  <div>
                    <h3>{crop.crop}</h3>
                    <small>{crop.type}</small>
                  </div>
                </div>
                <span className={`activity-badge activity-${crop.activityTone}`}>
                  {crop.activity}
                </span>
              </div>

              {crop.relevantNow && (
                <span className="crop-badge calendar-now-badge">Most relevant now</span>
              )}

              <div className="planting-calendar-meta">
                <span>
                  <strong>Best window</strong>
                  {crop.bestWindow}
                </span>
                <span>
                  <strong>Harvest</strong>
                  {crop.harvestWindow}
                </span>
                <span className={`frost-chip frost-${crop.frostTone}`}>
                  <strong>Frost</strong>
                  {crop.frostSensitivity}
                </span>
              </div>

              <p>{crop.notes}</p>
              <div className="planting-care-tip">
                <strong>Care tip</strong>
                {crop.careTip}
              </div>
            </article>
          ))}
        </div>

        {plantingCalendar.filteredCrops.length === 0 && (
          <div className="almanac-forecast-loading">
            No crops match those filters. Try another season or activity.
          </div>
        )}

        {plantingCalendar.filteredCrops.length > 0 && (
          <div className="planting-calendar-more">
            <span>
              Showing {plantingCalendar.visibleCrops.length} of{" "}
              {plantingCalendar.filteredCrops.length} crops
            </span>
            {(plantingCalendar.hasMore || plantingCalendar.visibleCrops.length > 6) && (
              <div className="planting-calendar-more-actions">
                {plantingCalendar.hasMore ? (
                <button
                  type="button"
                  className="btn-soft"
                  onClick={() => setCalendarVisibleCount((count) => count + 6)}
                >
                  Show more crops
                </button>
              ) : (
                <button
                  type="button"
                  className="btn-soft"
                  onClick={() => setCalendarVisibleCount(6)}
                >
                  Show fewer crops
                </button>
                )}
              </div>
            )}
          </div>
        )}
      </section>

      <section className="almanac-workbench-grid">
        <div className="almanac-calendar-panel">
          <div className="almanac-panel-heading">
            <span className="almanac-eyebrow">This month</span>
            <h2>Seasonal Reminders</h2>
            <p>{almanacData.monthName} checklist for field, market, and community timing.</p>
          </div>
          <ol className="almanac-reminder-list">
            {almanacData.reminders.slice(0, 6).map((reminder, index) => (
              <li key={reminder}>
                <span>{index + 1}</span>
                <p>{reminder}</p>
              </li>
            ))}
          </ol>
        </div>

        <div className="almanac-actions-panel">
          <div className="almanac-panel-heading">
            <span className="almanac-eyebrow">Jump back in</span>
            <h2>Quick Actions</h2>
          </div>
          <div className="almanac-action-grid">
            {actionLinks.map((action) => (
              <Link
                className={`almanac-action-card ${action.tone}`}
                key={action.title}
                to={action.to}
                state={action.state}
              >
                <span>{action.icon}</span>
                <strong>{action.title}</strong>
                <p>{action.description}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="almanac-notebook-section">
        <div className="almanac-notebook-heading">
          <span className="almanac-eyebrow">Field guide</span>
          <h2>Almanac Tips</h2>
          <p>Month-based notes for getting more out of MyFarmingPal this season.</p>
        </div>
        <div className="almanac-notes-board">
          {almanacData.tips.slice(0, 4).map((tip, index) => (
            <article className="almanac-note-card" key={tip}>
              <span>{String(index + 1).padStart(2, "0")}</span>
              <p>{tip}</p>
            </article>
          ))}
        </div>
      </section>
    </main>
  );
};

export default AlmanacPage;
